import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { getAvatarUrl } from '@/utils/storage';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Search, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { glassCardStatic } from '@/utils/admin-glass-styles';

interface DebugResult {
  step: string;
  status: 'success' | 'error' | 'info';
  message: string;
  data?: any;
}

export default function AvatarDebugTool() {
  const [userId, setUserId] = useState('');
  const [bucketName, setBucketName] = useState<'client.photo' | 'practitioner.photo'>('client.photo');
  const [isDebugging, setIsDebugging] = useState(false);
  const [debugResults, setDebugResults] = useState<DebugResult[]>([]);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const { toast } = useToast();

  const addDebugResult = (step: string, status: 'success' | 'error' | 'info', message: string, data?: any) => {
    const result: DebugResult = { step, status, message, data };
    setDebugResults(prev => [...prev, result]);
    console.log(`[AVATAR DEBUG] ${step}: ${message}`, data);
  };

  const debugUserAvatar = async () => {
    if (!userId) {
      toast({
        title: "参数缺失",
        description: "请输入用户ID",
        variant: "destructive"
      });
      return;
    }

    setIsDebugging(true);
    setDebugResults([]);
    setAvatarUrl('');

    try {
      addDebugResult('开始调试', 'info', `调试用户 ${userId} 的头像，存储桶: ${bucketName}`);

      // Step 1: Check if user profile exists
      const tableName = bucketName === 'client.photo' ? 'clients' : 'practitioners';
      const { data: userProfile, error: profileError } = await supabase
        .from(tableName)
        .select('id, first_name, last_name, avatar_url')
        .eq('id', userId)
        .single();

      if (profileError || !userProfile) {
        addDebugResult('用户查找', 'error', `用户不存在或无权访问: ${profileError?.message}`);
        return;
      }

      addDebugResult('用户查找', 'success', `找到用户: ${userProfile.first_name} ${userProfile.last_name}`, {
        avatar_url: userProfile.avatar_url
      });

      // Step 2: List files in user's storage folder
      const { data: files, error: listError } = await supabase.storage
        .from(bucketName)
        .list(userId);

      if (listError) {
        addDebugResult('Storage File List', 'error', `Unable to list files: ${listError.message}`);
      } else {
        addDebugResult('Storage File List', 'success', `Found ${files?.length || 0} files`, files);
      }

      // Step 3: Test avatar URL generation
      if (userProfile.avatar_url) {
        const generatedUrl = getAvatarUrl(bucketName, userProfile.avatar_url);
        setAvatarUrl(generatedUrl);
        addDebugResult('URL Generation', 'success', `Generated avatar URL: ${generatedUrl}`);

        // Step 4: Test URL accessibility
        try {
          const response = await fetch(generatedUrl, { method: 'HEAD' });
          if (response.ok) {
            addDebugResult('URL Accessibility', 'success', `Avatar file accessible (status: ${response.status})`);
          } else {
            addDebugResult('URL Accessibility', 'error', `Avatar file not accessible (status: ${response.status})`);
          }
        } catch (fetchError) {
          addDebugResult('URL Accessibility', 'error', `Network request failed: ${fetchError}`);
        }
      } else {
        addDebugResult('Avatar Path', 'info', 'User has not set an avatar');
      }

      // Step 5: Check storage bucket configuration
      const { data: buckets, error: bucketError } = await supabase.storage.listBuckets();
      if (bucketError) {
        addDebugResult('Bucket Config', 'error', `Unable to get bucket info: ${bucketError.message}`);
      } else {
        const targetBucket = buckets.find(b => b.name === bucketName);
        if (targetBucket) {
          addDebugResult('Bucket Config', 'success', `Bucket ${bucketName} configured correctly`, {
            public: targetBucket.public,
            created_at: targetBucket.created_at
          });
        } else {
          addDebugResult('Bucket Config', 'error', `Bucket ${bucketName} does not exist`);
        }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addDebugResult('Debug Process', 'error', errorMessage);
      toast({
        title: "Debug Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsDebugging(false);
    }
  };

  const clearResults = () => {
    setDebugResults([]);
    setAvatarUrl('');
  };

  const getStatusIcon = (status: 'success' | 'error' | 'info') => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'info':
        return <Search className="h-4 w-4 text-blue-600" />;
    }
  };

  const getStatusBadge = (status: 'success' | 'error' | 'info') => {
    const variants = {
      success: 'default',
      error: 'destructive',
      info: 'secondary'
    } as const;
    
    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  return (
    <Card className={`w-full max-w-4xl mx-auto ${glassCardStatic}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Avatar Debug Tool
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Debug Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="user-id">User ID</Label>
            <Input
              id="user-id"
              placeholder="Enter user UUID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bucket-name">Storage Bucket</Label>
            <select 
              className="w-full p-2 border rounded-md"
              value={bucketName}
              onChange={(e) => setBucketName(e.target.value as 'client.photo' | 'practitioner.photo')}
            >
              <option value="client.photo">client.photo</option>
              <option value="practitioner.photo">practitioner.photo</option>
            </select>
          </div>
        </div>

        {/* Debug Actions */}
        <div className="flex gap-2">
          <Button 
            onClick={debugUserAvatar} 
            disabled={isDebugging || !userId}
            className="flex items-center gap-2"
          >
            <Search className="h-4 w-4" />
            {isDebugging ? 'Debugging...' : 'Debug Avatar'}
          </Button>
          
          {debugResults.length > 0 && (
            <Button 
              variant="outline" 
              onClick={clearResults}
            >
              Clear Results
            </Button>
          )}
        </div>

        {/* Avatar Preview */}
        {avatarUrl && (
          <div className="space-y-2">
            <Label>Avatar Preview</Label>
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage 
                  src={avatarUrl} 
                  alt="Debug avatar"
                  onError={(e) => {
                    console.error("Debug avatar failed to load:", avatarUrl);
                  }}
                />
                <AvatarFallback>
                  <User size={24} />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-sm font-medium">Current Avatar URL:</p>
                <Textarea 
                  value={avatarUrl} 
                  readOnly 
                  className="text-xs"
                  rows={2}
                />
              </div>
            </div>
          </div>
        )}

        {/* Debug Results */}
        {debugResults.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Debug Results</h3>
              <Badge variant="outline">{debugResults.length} records</Badge>
            </div>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {debugResults.map((result, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-3 border rounded-lg bg-muted/30"
                >
                  {getStatusIcon(result.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{result.step}</span>
                      {getStatusBadge(result.status)}
                    </div>
                    <p className="text-sm text-muted-foreground break-words">{result.message}</p>
                    {result.data && (
                      <details className="mt-2">
                        <summary className="text-xs text-muted-foreground cursor-pointer">View details</summary>
                        <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Debug Steps:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Enter the user ID to debug (UUID format)</li>
            <li>• Select the corresponding storage bucket (client or practitioner)</li>
            <li>• Click "Debug Avatar" button</li>
            <li>• Review debug results to understand avatar loading issues</li>
            <li>• Check bucket configuration and file permissions</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}